import { handleError } from '@/actions/metaActions';
import { clearVSRLookup, getVSRForCoordinate } from '@/actions/overlayActions';
import { HistoricalRoadReference } from '@/domain/HistoricalRoadReference';
import { Vegposisjon, Vegsystemreferanse } from '@/domain/omrader';
import { fetchHistoricalRoadReferences } from '@/middleware/fetchMisc';
import {
    measurementActive,
    stateConfig,
    statePreferences,
    stateVegsystemreferanseSok,
} from '@/selectors/selectors';
import { Severity, VegkartError, VegsystemreferanseLookup } from '@/state';
import { useMap } from '@/state/context/MapContext';
import { Dispatch, useDispatch, useSelector } from '@/state/store';
import { getRoadReferenceFiksURL } from '@/utils/FiksVegDataHelper';
import { roadRefCoordinates } from '@/utils/SpatialHelper';
import { getVegBilderURL } from '@/utils/VegbilderHelper';
import { createVegrefInfoBoxContent } from '@/utils/VegsystemreferanseExplanation';
import InfoBox from '@/components/common/InfoBox';
import copy from 'clipboard-copy';
import { Feature, MapBrowserEvent, Overlay } from 'ol';
import { Geometry } from 'ol/geom';
import OLMap from 'ol/Map';
import { unByKey } from 'ol/Observable';
import { useEffect, useMemo, useState } from 'react';
import { OmraderFeatureLayer } from '../layers/OmraderFeatureLayer';
import RenderFeature from 'ol/render/Feature';
import { SelectionHandledBrowserEventKey } from '../handlers/FeatureSelectionHandler';

export const RoadReferencePopup = () => {
    const { map } = useMap();
    const [copied, setCopied] = useState(false);
    const [showHistoricalReferences, setShowHistoricalReferences] = useState(false);
    const [historicalReferences, setHistoricalReferences] = useState<
        HistoricalRoadReference[] | null
    >(null);
    const [historicalReferencesLoading, setHistoricalReferencesLoading] = useState(false);
    const [historicalReferencesFailed, setHistoricalReferencesFailed] = useState(false);
    const dispatch = useDispatch();
    const preferences = useSelector(statePreferences);
    const isDrawing = useSelector(measurementActive);
    const vegsystemreferansesoek = useSelector(stateVegsystemreferanseSok);
    const config = useSelector(stateConfig);
    const disabled = useMemo(() => {
        const meterAvgrensning: Vegposisjon | null =
            vegsystemreferansesoek?.vegsystemreferanse?.strekning?.meterAvgrensning ??
            null;

        return !(meterAvgrensning && !meterAvgrensning.slutt);
    }, [vegsystemreferansesoek]);

    useEffect(() => {
        setShowHistoricalReferences(false);
        setHistoricalReferences(null);
        setHistoricalReferencesLoading(false);
        setHistoricalReferencesFailed(false);
    }, [vegsystemreferansesoek]);

    useEffect(() => {
        if (!isDrawing) {
            const key = addMapEventListener(map, setCopied, dispatch);
            return () => {
                unByKey(key);
            };
        }
    }, [map, dispatch, isDrawing]);
    if (!vegsystemreferansesoek) {
        return <div />;
    }

    const roadReference = vegsystemreferansesoek.vegsystemreferanse;
    const vegsystemreferanse: string | null = roadReference
        ? selectText(preferences, roadReference, vegsystemreferansesoek.kommune)
        : null;
    const endDate = vegsystemreferansesoek.endDate;
    const coordinates = roadRefCoordinates(vegsystemreferansesoek);

    const handleCopy = async (text: string) => {
        try {
            await copy(text);
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2_000);
        } catch {
            dispatch(
                handleError(
                    new VegkartError('Could not copy to clipboard', {}, Severity.WARN)
                )
            );
        }
    };

    const loadHistoricalReferences = async () => {
        if (
            !vegsystemreferansesoek.veglenke ||
            historicalReferencesLoading ||
            historicalReferences
        )
            return;

        setHistoricalReferencesLoading(true);
        setHistoricalReferencesFailed(false);
        try {
            const references = await dispatch(
                fetchHistoricalRoadReferences(vegsystemreferansesoek.veglenke)
            );
            setHistoricalReferences(references);
        } catch (e) {
            setHistoricalReferencesFailed(true);
            dispatch(
                handleError(
                    new VegkartError(
                        'Feil ved henting av historiske vegreferanser.',
                        { error: e },
                        Severity.ERROR
                    )
                )
            );
        } finally {
            setHistoricalReferencesLoading(false);
        }
    };

    const popup = new Overlay({
        position: coordinates ?? undefined,
        positioning: 'top-left',
        stopEvent: true,
    });

    map.addOverlay(popup);
    return (
        <div
            className="o-popup"
            ref={popupElement => popup.setElement(popupElement ?? undefined)}
        >
            <div className="o-popup__arrow" />
            <header className="o-popup__header">
                <h3 className="o-popup__header-text">Vegsystemreferanse</h3>
                <button
                    type="button"
                    // we use onMouseUp because of quirkiness in openLayers
                    // https://github.com/openlayers/openlayers/issues/6948
                    onMouseUp={() => {
                        dispatch(clearVSRLookup());
                        map.removeOverlay(popup);
                    }}
                    title="Lukk"
                    aria-label="Lukk vegsystemreferansemarkør"
                    className="o-popup__close-button"
                >
                    <i className="o-icon-remove o-popup__remove-icon" />
                </button>
            </header>

            <section
                className={`o-popup__body${showHistoricalReferences ? ' o-popup__body--history-open' : ''}`}
            >
                {vegsystemreferanse && roadReference ? (
                    <>
                        <div className="o-popup__body-content">
                            <div className="o-popup__reference-text">
                                <div>{vegsystemreferanse}</div>
                                {endDate && (
                                    <span style={{ color: '#aaa' }}>
                                        Gyldig til {endDate}
                                    </span>
                                )}
                            </div>

                            <div className="o-popup__icon-container">
                                <InfoBox className="o-popup__icon o-popup___info-box">
                                    {createVegrefInfoBoxContent(
                                        roadReference,
                                        config.vegref_format_url
                                    )}
                                </InfoBox>

                                <button
                                    type="button"
                                    onClick={() => handleCopy(vegsystemreferanse)}
                                    title="Kopier vegsystemreferanse til utklippstavle"
                                >
                                    <i className="o-icon-copy o-popup__icon" />
                                </button>

                                {coordinates && (
                                    <a
                                        href={getVegBilderURL(coordinates)}
                                        title="Naviger til vegbilder"
                                        id="naviger_til_vegbilder"
                                        target={'_blank'}
                                        rel="noreferrer"
                                    >
                                        <i className="o-icon-camera o-popup__icon" />
                                    </a>
                                )}

                                {!disabled ? (
                                    <a
                                        href={getRoadReferenceFiksURL(
                                            roadReference,
                                            config
                                        )}
                                        title="Meld feil på veg"
                                        aria-label="Naviger til fiksvegdata"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <i className="o-icon-report o-popup__icon" />
                                    </a>
                                ) : (
                                    <i
                                        title="Melding av feil krever punktreferanse"
                                        aria-label="Naviger til fiksvegdata"
                                        style={{ paddingBottom: '2px' }}
                                        className="o-icon-report--disabled o-popup__icon"
                                    />
                                )}

                                <button
                                    type="button"
                                    title="Søk etter historisk vegreferanse"
                                    aria-label="Søk etter historisk vegreferanse"
                                    aria-pressed={showHistoricalReferences}
                                    disabled={!vegsystemreferansesoek.veglenke}
                                    onClick={() => {
                                        const nextShowHistoricalReferences =
                                            !showHistoricalReferences;
                                        setShowHistoricalReferences(
                                            nextShowHistoricalReferences
                                        );
                                        if (nextShowHistoricalReferences)
                                            loadHistoricalReferences();
                                    }}
                                >
                                    <i
                                        className="material-icons o-popup__icon o-popup__material-icon"
                                        aria-hidden="true"
                                    >
                                        history
                                    </i>
                                </button>
                            </div>
                        </div>
                        {showHistoricalReferences && (
                            <HistoricalRoadReferences
                                references={historicalReferences}
                                loading={historicalReferencesLoading}
                                failed={historicalReferencesFailed}
                                roadLinkPosition={
                                    vegsystemreferansesoek.veglenke?.kortform
                                }
                                coordinate={vegsystemreferansesoek.geometri?.wkt}
                                onCopy={handleCopy}
                            />
                        )}
                        {copied && <div className="o-popup__copied-status">Kopiert!</div>}
                    </>
                ) : (
                    'Vegsystemreferanse mangler'
                )}
            </section>
        </div>
    );
};

export const addMapEventListener = (
    map: OLMap,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>,
    dispatch: Dispatch
) => {
    return map.on('singleclick', event => {
        const selectionHandled =
            map.get(SelectionHandledBrowserEventKey) === event.originalEvent;
        map.unset(SelectionHandledBrowserEventKey);
        if (selectionHandled) return;

        const selectableFeatureAtPixel = map.forEachFeatureAtPixel(
            event.pixel,
            (f: Feature<Geometry> | RenderFeature) => {
                if (f instanceof RenderFeature) {
                    // if vector based tile
                    return false;
                } else {
                    return !(f as Feature<Geometry>).get(
                        OmraderFeatureLayer.IsAreaFeatureKey
                    );
                }
            }
        );
        if (!selectableFeatureAtPixel) {
            setCopied(false);
            dispatch(getVSRForCoordinate(event.coordinate as [number, number]));
        }
    });
};

const selectText = (
    preferences: { includeTrafikantgruppe: boolean },
    vegsystemreferanse: Vegsystemreferanse,
    kommune: number | null | undefined
) => {
    if (preferences.includeTrafikantgruppe)
        return transformRoadSysRef(vegsystemreferanse, kommune);
    else return transformRoadSysRefNoGroup(vegsystemreferanse, kommune);
};

const transformRoadSysRef = (
    vegsystemreferanse: Vegsystemreferanse,
    kommune: number | null | undefined
) => {
    if (['K', 'P', 'S'].includes(vegsystemreferanse.vegsystem?.vegkategori ?? ''))
        return kommune + ' ' + vegsystemreferanse.toVegkartKortform();
    else return vegsystemreferanse.toVegkartKortform();
};

const transformRoadSysRefNoGroup = (
    vegsystemreferanse: Vegsystemreferanse,
    kommune: number | null | undefined
) => {
    if (['K', 'P', 'S'].includes(vegsystemreferanse.vegsystem?.vegkategori ?? ''))
        return kommune + ' ' + vegsystemreferanse.toVegkartRedusertKortform();
    else return vegsystemreferanse.toVegkartRedusertKortform();
};

type HistoricalRoadReferencesProps = {
    references: HistoricalRoadReference[] | null;
    loading: boolean;
    failed: boolean;
    roadLinkPosition?: string;
    coordinate?: string;
    onCopy: (text: string) => void;
};

const HistoricalRoadReferences = ({
    references,
    loading,
    failed,
    roadLinkPosition,
    coordinate,
    onCopy,
}: HistoricalRoadReferencesProps) => {
    return (
        <div className="o-popup__history">
            <div className="o-popup__history-title">Historiske vegreferanser</div>
            {loading && <div className="o-popup__history-status">Søker...</div>}
            {failed && (
                <div className="o-popup__history-status">
                    Kunne ikke hente historiske vegreferanser.
                </div>
            )}
            {!loading && !failed && references?.length === 0 && (
                <div className="o-popup__history-status">
                    Ingen historiske vegreferanser funnet.
                </div>
            )}
            {!loading && !failed && references !== null && references.length > 0 && (
                <div className="o-popup__history-sequence">
                    {references.map(reference => (
                        <div
                            className="o-popup__history-entry"
                            key={
                                reference.roadReference +
                                reference.fromDate +
                                reference.toDate
                            }
                        >
                            <div className="o-popup__history-date-separator">
                                {formatNorwegianDate(reference.toDate)}
                                <span
                                    className="o-popup__history-date-separator-dash"
                                    aria-hidden="true"
                                >
                                    -
                                </span>
                                {formatNorwegianDate(reference.fromDate)}
                            </div>
                            <div className="o-popup__history-item">
                                <div className="o-popup__history-reference-row">
                                    <div className="o-popup__history-reference">
                                        {reference.roadReference}
                                    </div>
                                    <button
                                        type="button"
                                        className="o-popup__history-copy-button"
                                        onClick={() => onCopy(reference.roadReference)}
                                        title="Kopier vegreferanse til utklippstavle"
                                        aria-label={`Kopier ${reference.roadReference}`}
                                    >
                                        <i className="o-icon-copy o-popup__history-copy-icon" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="o-popup__history-details">
                <HistoricalRoadReferenceDetail
                    label="Veglenkeposisjon"
                    value={roadLinkPosition}
                    onCopy={onCopy}
                />
                <HistoricalRoadReferenceDetail
                    label="Koordinat"
                    value={formatCoordinate(coordinate)}
                    onCopy={onCopy}
                />
            </div>
        </div>
    );
};

const HistoricalRoadReferenceDetail = ({
    label,
    value,
    onCopy,
}: {
    label: string;
    value?: string;
    onCopy: (text: string) => void;
}) => {
    if (!value) return null;

    return (
        <div className="o-popup__history-detail-row">
            <div className="o-popup__history-detail-label">{label}</div>
            <div className="o-popup__history-reference-row">
                <div className="o-popup__history-detail-value">{value}</div>
                <button
                    type="button"
                    className="o-popup__history-copy-button"
                    onClick={() => onCopy(value)}
                    title={`Kopier ${label.toLowerCase()} til utklippstavle`}
                    aria-label={`Kopier ${value}`}
                >
                    <i className="o-icon-copy o-popup__history-copy-icon" />
                </button>
            </div>
        </div>
    );
};

const formatNorwegianDate = (date: string | null) => {
    if (!date) return 'i dag';
    return new Date(date).toLocaleDateString('nb-NO');
};

const formatCoordinate = (coordinate?: string) => {
    if (!coordinate) return undefined;

    return coordinate.replace(/^POINT(?:\s+Z)?\s*\((.+)\)$/, '$1');
};
